import './App.css';
import { useState, useEffect, useRef } from 'react';
// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics, logEvent } from "firebase/analytics";
import { getFirestore, doc, setDoc, getDoc, increment, serverTimestamp } from "firebase/firestore";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyBaPuCdflLnZJkpEoEUZ6DTKCcQ9CmdLo4",
  authDomain: "turea-2452b.firebaseapp.com",
  projectId: "turea-2452b",
  storageBucket: "turea-2452b.firebasestorage.app",
  messagingSenderId: "406829970034",
  appId: "1:406829970034:web:807daf84c8b1f8bc5288eb",
  measurementId: "G-TWSJ9LVHX6"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const db = getFirestore(app);

function App() {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [downloadCount, setDownloadCount] = useState(0);
  const [copyCount, setCopyCount] = useState({});
  const [showAdmin, setShowAdmin] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Refs for scroll animation elements
  const techniquesRef = useRef(null);
  const headerRef = useRef(null);
  const mainRef = useRef(null);

  // Handle scroll events for animations
  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
      
      // Parallax effect - adjust intensity based on screen size
      if (headerRef.current) {
        const scrollPosition = window.scrollY;
        const isMobile = window.innerWidth <= 768;
        // Use a smaller multiplier for mobile to reduce movement
        const parallaxIntensity = isMobile ? 0.05 : 0.2;
        headerRef.current.style.transform = `translateY(${scrollPosition * parallaxIntensity}px)`;
      }
      
      // Reveal animations
      const revealElements = document.querySelectorAll('.reveal');
      revealElements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('active');
        }
      });
    };
    
    // Handle resize events
    const handleResize = () => {
      // Recalculate on resize
      handleScroll();
    };
    
    // Add scroll event listener with passive option for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Initial check for elements in view
    setTimeout(() => {
      handleScroll();
    }, 300);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // Track page visit
    const trackPageVisit = async () => {
      try {
        // Track in Firebase Analytics
        logEvent(analytics, 'page_view', {
          page_title: 'ChatGPT Prompt Cheat Sheet',
          page_location: window.location.href
        });
        
        // Also update visit counter in Firestore
        const visitsRef = doc(db, "analytics", "visits");
        await setDoc(visitsRef, { 
          count: increment(1),
          last_visit: serverTimestamp()
        }, { merge: true });
      } catch (error) {
        console.error("Error tracking page visit:", error);
      }
    };

    // Get download count from localStorage and Firebase
    const fetchDownloadCount = async () => {
      try {
        // First check local storage for cached value
        const storedCount = localStorage.getItem('pdfDownloadCount') || 0;
        setDownloadCount(Number(storedCount));
        
        // Then try to get the latest count from Firestore
        const docRef = doc(db, "analytics", "downloads");
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const firebaseCount = docSnap.data().count;
          setDownloadCount(firebaseCount);
          localStorage.setItem('pdfDownloadCount', firebaseCount.toString());
        } else {
          // If no document exists yet, initialize with the localStorage count
          await setDoc(docRef, { count: Number(storedCount) });
        }
        
        // Get copy counts
        const copyRef = doc(db, "analytics", "copies");
        const copySnap = await getDoc(copyRef);
        
        if (copySnap.exists()) {
          setCopyCount(copySnap.data());
        }
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      }
    };
    
    trackPageVisit();
    fetchDownloadCount();
  }, []);

  const handleCopy = async (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    
    try {
      // Track in Firebase Analytics
      logEvent(analytics, 'copy_prompt', {
        prompt_index: index
      });
      
      // Track in Firestore
      const promptType = getPromptTypeFromIndex(index);
      const copyRef = doc(db, "analytics", "copies");
      
      // Update copy counts
      await setDoc(copyRef, { 
        total: increment(1),
        [promptType]: increment(1),
        last_copy: serverTimestamp()
      }, { merge: true });
      
      // Update local copy count state
      setCopyCount(prev => ({
        ...prev,
        total: (prev.total || 0) + 1,
        [promptType]: (prev[promptType] || 0) + 1
      }));
    } catch (error) {
      console.error("Error tracking copy event:", error);
    }
  };

  const getPromptTypeFromIndex = (index) => {
    // Map index to prompt category
    const categories = [
      "writing_essay", "writing_improve", "writing_ideas",
      "programming_debug", "programming_explain", "programming_convert",
      "learning_summary", "learning_compare", "learning_guide"
    ];
    return categories[index] || `prompt_${index}`;
  };

  const handleDownload = async () => {
    try {
      // Increment locally
      const newCount = downloadCount + 1;
      setDownloadCount(newCount);
      localStorage.setItem('pdfDownloadCount', newCount.toString());
      
      // Track in Firebase Analytics
      logEvent(analytics, 'pdf_download');
      
      // Update in Firestore
      const docRef = doc(db, "analytics", "downloads");
      await setDoc(docRef, { 
        count: increment(1),
        last_download: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error("Error updating download count:", error);
    }
  };

  const toggleCategory = (categoryId) => {
    // Track category expansion in analytics
    logEvent(analytics, 'expand_category', {
      category: categoryId,
      action: expandedCategories[categoryId] ? 'collapse' : 'expand'
    });
    
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleAdminPanel = () => {
    setShowAdmin(prev => !prev);
  };

  const getMostCopiedPrompt = () => {
    if (!copyCount || Object.keys(copyCount).length <= 1) return 'No data yet';
    
    let highest = 0;
    let highestType = '';
    
    Object.entries(copyCount).forEach(([key, value]) => {
      if (key !== 'total' && value > highest) {
        highest = value;
        highestType = key;
      }
    });
    
    return highestType.replace('_', ' ') + ' (' + highest + ')';
  };

  return (
    <div className="App">
      {/* Scroll Progress Indicator */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }}></div>

      <div className="background-elements">
        <div className="circle c1"></div>
        <div className="circle c2"></div>
        <div className="circle c3"></div>
        <div className="square s1"></div>
        <div className="square s2"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>

      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-logo">Turea</div>
          <div className="navbar-links">
            <a href="#categories">Categories</a>
            <a href="#techniques">Techniques</a>
            <a href="tutorial.pdf" download className="download-btn-small" onClick={handleDownload}>
              <span className="download-icon-small">↓</span> PDF
            </a>
          </div>
        </div>
      </nav>

      <header ref={headerRef} className="App-header parallax">
        <h1>ChatGPT Prompt Cheat Sheet</h1>
        <p className="tagline">Master prompting techniques for better results</p>
        <a href="tutorial.pdf" download className="download-btn" onClick={handleDownload}>
          <span className="download-icon">↓</span> Download PDF Guide
        </a>
        <div className="download-counter">
          {downloadCount} people have downloaded this guide
        </div>
      </header>
      
      <main ref={mainRef} className="App-main">
        <section id="categories" className="prompt-categories">
          <div className="category reveal">
            <h2 className="category-title">Writing Assistance</h2>
            <div className="prompt-cards">
              <div className="prompt-card">
                <h3>Essay Structure</h3>
                <div className="prompt-text">
                  "I need to write a 1500-word persuasive essay about climate change solutions. Create a detailed outline with an introduction, three main arguments with supporting evidence, counterarguments, and conclusion. Focus on practical policy changes."
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 0 ? 'copied' : ''}`}
                  onClick={() => handleCopy("I need to write a 1500-word persuasive essay about climate change solutions. Create a detailed outline with an introduction, three main arguments with supporting evidence, counterarguments, and conclusion. Focus on practical policy changes.", 0)}
                >
                  {copiedIndex === 0 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Include word count, essay type, and specific structure requirements</span>
                </div>
              </div>
              
              <div className="prompt-card">
                <h3>Improve Writing</h3>
                <div className="prompt-text">
                  "Rewrite this paragraph to make it more concise and engaging for a business audience. Remove jargon and use active voice: [paste your text here]"
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 1 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Rewrite this paragraph to make it more concise and engaging for a business audience. Remove jargon and use active voice: [paste your text here]", 1)}
                >
                  {copiedIndex === 1 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Specify the target audience and the exact style improvements you want</span>
                </div>
              </div>
              
              <div className={`prompt-card ${!expandedCategories.writing && 'mobile-hidden'}`}>
                <h3>Generate Ideas</h3>
                <div className="prompt-text">
                  "Generate 10 creative content ideas for a social media campaign promoting sustainable fashion. Include potential hashtags, post types (video/image/carousel), and a brief description of each idea. The target audience is eco-conscious millennials."
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 2 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Generate 10 creative content ideas for a social media campaign promoting sustainable fashion. Include potential hashtags, post types (video/image/carousel), and a brief description of each idea. The target audience is eco-conscious millennials.", 2)}
                >
                  {copiedIndex === 2 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Define your audience, specific output format, and content purpose</span>
                </div>
              </div>
              <button 
                className="load-more-btn" 
                onClick={() => toggleCategory('writing')}
              >
                {expandedCategories.writing ? 'Show Less' : 'Show More'}
              </button>
            </div>
          </div>
          
          <div className="category reveal">
            <h2 className="category-title">Programming Help</h2>
            <div className="prompt-cards">
              <div className="prompt-card">
                <h3>Debug Code</h3>
                <div className="prompt-text">
                  "Debug this React component that's causing an infinite re-rendering loop. Explain each issue you find and provide the corrected code with comments explaining the fixes: [paste your code here]"
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 3 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Debug this React component that's causing an infinite re-rendering loop. Explain each issue you find and provide the corrected code with comments explaining the fixes: [paste your code here]", 3)}
                >
                  {copiedIndex === 3 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Specify the exact problem you're experiencing and what you've tried</span>
                </div>
              </div>
              
              <div className="prompt-card">
                <h3>Explain Concept</h3>
                <div className="prompt-text">
                  "Explain how JavaScript closures work to a junior developer. Include a practical example showing their usefulness in real-world applications, and explain common pitfalls to avoid."
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 4 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Explain how JavaScript closures work to a junior developer. Include a practical example showing their usefulness in real-world applications, and explain common pitfalls to avoid.", 4)}
                >
                  {copiedIndex === 4 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Specify your experience level and what aspects you need explained</span>
                </div>
              </div>
              
              <div className={`prompt-card ${!expandedCategories.programming && 'mobile-hidden'}`}>
                <h3>Convert Code</h3>
                <div className="prompt-text">
                  "Convert this Python function that processes data from a CSV file into JavaScript. Use modern ES6 features, maintain the same functionality, and optimize for performance. Add comments explaining key differences between the implementations: [paste Python code here]"
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 5 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Convert this Python function that processes data from a CSV file into JavaScript. Use modern ES6 features, maintain the same functionality, and optimize for performance. Add comments explaining key differences between the implementations: [paste Python code here]", 5)}
                >
                  {copiedIndex === 5 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Request specific features of the target language you want utilized</span>
                </div>
              </div>
              <button 
                className="load-more-btn" 
                onClick={() => toggleCategory('programming')}
              >
                {expandedCategories.programming ? 'Show Less' : 'Show More'}
              </button>
            </div>
          </div>
          
          <div className="category reveal">
            <h2 className="category-title">Learning & Research</h2>
            <div className="prompt-cards">
              <div className="prompt-card">
                <h3>Topic Summary</h3>
                <div className="prompt-text">
                  "Create a comprehensive summary of quantum computing covering: key principles, current technological state, major breakthroughs in the last 5 years, practical applications, and future outlook. Format it with clear headings and bullet points for key takeaways."
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 6 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Create a comprehensive summary of quantum computing covering: key principles, current technological state, major breakthroughs in the last 5 years, practical applications, and future outlook. Format it with clear headings and bullet points for key takeaways.", 6)}
                >
                  {copiedIndex === 6 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: List specific aspects you want covered and preferred format</span>
                </div>
              </div>
              
              <div className="prompt-card">
                <h3>Compare & Contrast</h3>
                <div className="prompt-text">
                  "Compare and contrast microservices vs. monolithic architecture in terms of: scalability, maintenance complexity, deployment speed, fault isolation, and development team structure. Create a table summarizing the key differences, and recommend when to choose each approach."
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 7 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Compare and contrast microservices vs. monolithic architecture in terms of: scalability, maintenance complexity, deployment speed, fault isolation, and development team structure. Create a table summarizing the key differences, and recommend when to choose each approach.", 7)}
                >
                  {copiedIndex === 7 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Specify exact comparison criteria and desired output format</span>
                </div>
              </div>
              
              <div className={`prompt-card ${!expandedCategories.learning && 'mobile-hidden'}`}>
                <h3>Step-by-Step Guide</h3>
                <div className="prompt-text">
                  "Create a detailed step-by-step guide for setting up a secure WordPress site for a small business. Include security best practices, essential plugins, SEO optimization steps, and performance tuning. Assume I have basic technical knowledge but am not a developer."
                </div>
                <button 
                  className={`copy-btn ${copiedIndex === 8 ? 'copied' : ''}`}
                  onClick={() => handleCopy("Create a detailed step-by-step guide for setting up a secure WordPress site for a small business. Include security best practices, essential plugins, SEO optimization steps, and performance tuning. Assume I have basic technical knowledge but am not a developer.", 8)}
                >
                  {copiedIndex === 8 ? '✓ Copied!' : 'Copy'}
                </button>
                <div className="prompt-tips">
                  <span>Tip: Specify your experience level and what aspects need the most detail</span>
                </div>
              </div>
              <button 
                className="load-more-btn" 
                onClick={() => toggleCategory('learning')}
              >
                {expandedCategories.learning ? 'Show Less' : 'Show More'}
              </button>
            </div>
          </div>
        </section>
        
        <section ref={techniquesRef} id="techniques" className="prompt-techniques reveal">
          <div className="section-title-wrapper">
            <h2 className="section-title">Advanced Techniques</h2>
          </div>
          <div className="techniques-grid">
            <div className="technique">
              <h3>System Role Prompting</h3>
              <p>Start with "Act as an expert [profession/role]" to get responses from a specific perspective or expertise level.</p>
            </div>
            <div className="technique">
              <h3>Chain of Thought</h3>
              <p>Add "Think step by step" or "Walk through your reasoning" to get more detailed logical analysis and problem-solving.</p>
            </div>
            <div className="technique">
              <h3>Few-Shot Examples</h3>
              <p>Provide 2-3 examples of input/output pairs to teach ChatGPT the exact format and style you want for responses.</p>
            </div>
            <div className="technique">
              <h3>Output Formatting</h3>
              <p>Request specific formats like "Respond using a markdown table" or "Format as JSON" for structured, usable results.</p>
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer - placed directly inside App container */}
      <footer className="App-footer">
        <div className="footer-content">
          <p>Create better prompts, get better responses!</p>
          <p>&copy; {new Date().getFullYear()} Turea AI Tools. All Rights Reserved.</p>
        </div>
      </footer>

      {showAdmin && (
        <div className="admin-panel">
          <h2>Admin Analytics Dashboard</h2>
          <div className="analytics-data">
            <div className="analytics-card">
              <h3>Downloads</h3>
              <p className="analytics-number">{downloadCount}</p>
            </div>
            <div className="analytics-card">
              <h3>Total Copies</h3>
              <p className="analytics-number">{copyCount.total || 0}</p>
            </div>
            <div className="analytics-card">
              <h3>Most Copied</h3>
              <p className="analytics-info">
                {getMostCopiedPrompt()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Admin button - hidden in UI but accessible */}
      <button 
        className="admin-button"
        onClick={toggleAdminPanel}
      >
        {showAdmin ? 'Close Admin' : ''}
      </button>
    </div>
  );
}

export default App;
